import classNames from 'classnames';
import { observer } from 'mobx-react';
import { useTranslation } from 'react-i18next';

import Button from '../../../../components/buttons/Button';
import { DialogHeader } from '../../../../components/Dialog/DialogUiRedesign/DialogHeader';
import type { WizardChildProps } from '../../../../components/Wizard/types';
import { CONTACT_SUPPORT_URL } from '../../../../config/constants';
import { useMediaQuery } from '../../../../hooks/useMediaQuery';
import { analytics } from '../../../../services/analytics';
import { useStores } from '../../../../stores/useStores';
import { useOpenAuthWizard } from '../../useOpenAuthWizard';
import { HeaderLeftElement } from '../HeaderLeftElement/HeaderLeftElement';
import { RedesignContainer } from '../RedesignContainer/RedesignContainer';

import styles from './ResetPasswordSuccessRedesign.module.scss';

export const ResetPasswordSuccessRedesign = observer(function ResetPasswordSuccessRedesign(props: WizardChildProps) {
  const { t } = useTranslation();

  const { authStore, portalStore } = useStores();

  const { openAuthWizard } = useOpenAuthWizard();

  const landingKey = authStore.getLandingKey();

  const isDesktop = useMediaQuery('(min-width: 768px)');

  const contactSupport = () => {
    window.open(CONTACT_SUPPORT_URL, '_blank', 'noopener,noreferrer');
    void analytics.sendEvent('reset_password', {
      event_action: 'click',
      event_category: 'sign_up',
      event_type: 'reset_password',
      event_value: 'contact_support',
      button_text: 'contact support',
    });
  };

  const handleOpenSignUp = () => {
    openAuthWizard('signUpWizard', {
      triggeredBy: 'reset_password',
      force: true,
      persistent: false,
      landingKey: landingKey ?? undefined,
    });
    void analytics.sendEvent('signup_flow', {
      event_action: 'click',
      event_category: 'sign_up',
    });
  };

  return (
    <RedesignContainer className={styles.container}>
      <DialogHeader
        className={styles.dialogHeader}
        closeBtnDataTestId="reset-password-close-btn"
        decorationsWrapperClassName={styles.dialogHeaderDecorationsWrapperClassName}
        goBackBtnDataTestId="reset-password-back-btn"
        headerLeftElement={<HeaderLeftElement showLogo={isDesktop} />}
        title={t('auth.resetPassword')}
        {...(isDesktop ? { onClose: portalStore.closePortal } : { onGoBack: props.prevStep })}
      />
      <div className={styles.containerContent}>
        <div className={styles.rowLineContainer}>
          <span className={styles.rowLineTitle}>{t('auth.passwordResetEmailSent')}</span>
          <span className={styles.rowLineText}>{t('auth.checkSpamOrContactSupport')}</span>
        </div>

        <div className={styles.contactSupportButtonWrapper}>
          <Button
            btnType="purple"
            className={styles.contactSupportButton}
            dataTestId="reset-password-support-btn"
            onClick={contactSupport}
          >
            {t('common.contactSupportUpperCase')}
          </Button>
        </div>

        <div
          className={classNames({
            'animation-slide-down': true,
            'form-links': true,
          })}
        >
          <span>{t('auth.doNotHaveAccountYet')}</span>
          <span className={styles.signUpLink} data-testid="lobby-signup-btn" onClick={handleOpenSignUp}>
            {t('auth.signUpV2')}
          </span>
        </div>
      </div>
    </RedesignContainer>
  );
});
